import { useEffect, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CadenceStepRow } from '@/components/organisms/settings-reminders/CadenceStepRow';
import {
  duplicateCadenceStep,
  type CadenceFormStep,
} from '@/components/organisms/settings-reminders/cadence-utils';
import type { TemplateConfig } from '@/types/reminder-policy';

interface CadenceEditorProps {
  value: CadenceFormStep[];
  onChange: (next: CadenceFormStep[]) => void;
  templates?: TemplateConfig[];
  errors?: Array<string | undefined>;
  rootError?: string;
  readOnly?: boolean;
  autoExpandId?: string | null;
}

export function CadenceEditor({
  value,
  onChange,
  templates = [],
  errors,
  rootError,
  readOnly,
  autoExpandId,
}: CadenceEditorProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [expandedStepIds, setExpandedStepIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setExpandedStepIds((current) => {
      const ids = new Set(value.map((step) => step.id));
      const next = new Set([...current].filter((id) => ids.has(id)));
      const changed = next.size !== current.size || [...next].some((id) => !current.has(id));
      return changed ? next : current;
    });
  }, [value]);

  useEffect(() => {
    if (autoExpandId) {
      setExpandedStepIds((current) => new Set(current).add(autoExpandId));
    }
  }, [autoExpandId]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = value.findIndex((step) => step.id === active.id);
    const newIndex = value.findIndex((step) => step.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(value, oldIndex, newIndex));
  }

  function updateStep(index: number, next: CadenceFormStep) {
    onChange(value.map((step, i) => (i === index ? next : step)));
  }

  function removeStep(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function duplicateStep(index: number) {
    const copy = duplicateCadenceStep(value[index]);
    onChange([...value.slice(0, index + 1), copy, ...value.slice(index + 1)]);
    setExpandedStepIds((current) => new Set(current).add(copy.id));
  }

  function toggleExpanded(stepId: string) {
    setExpandedStepIds((current) => {
      const next = new Set(current);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  }

  if (value.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
        No steps yet. Use the + button to add one.
        {rootError && <p className="mt-1 text-destructive">{rootError}</p>}
      </div>
    );
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={value.map((step) => step.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="overflow-hidden">
            {value.map((step, index) => (
              <CadenceStepRow
                key={step.id}
                step={step}
                index={index}
                templates={templates}
                onChange={(next) => updateStep(index, next)}
                onRemove={() => removeStep(index)}
                onDuplicate={() => duplicateStep(index)}
                expanded={expandedStepIds.has(step.id) || Boolean(errors?.[index])}
                onToggleExpanded={() => toggleExpanded(step.id)}
                canRemove={!readOnly && value.length > 1}
                readOnly={readOnly}
                error={errors?.[index]}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {rootError && <p className="px-4 pb-3 text-xs text-destructive">{rootError}</p>}
    </>
  );
}
